/** @jsx jsx */
import { jsx } from '@emotion/react';

import { useQuery } from '@apollo/client';

import { useLocation } from 'js/lib/useRouter';
import user from 'js/lib/user';

import { Grid, PageGridContainer } from '@coursera/cds-core';

import { NotFound } from 'bundles/common/components/withNotFound';
import CourseCardsSection from 'bundles/partners-graphql/components/cds/CourseCardsSection';
import DegreeCardsSection from 'bundles/partners-graphql/components/cds/DegreeCardsSection';
import ExploreCategoriesSection from 'bundles/partners-graphql/components/cds/ExploreCategoriesSection';
import InstructorsCarouselSection from 'bundles/partners-graphql/components/cds/InstructorsCarouselSection';
import Loading from 'bundles/partners-graphql/components/cds/Loading';
import PartnerHero from 'bundles/partners-graphql/components/cds/PartnerHero';
import PartnerLinksSection from 'bundles/partners-graphql/components/cds/PartnerLinks';
import SpecializationCardsSection from 'bundles/partners-graphql/components/cds/SpecializationCardsSection';
import {
  buildPartnerCourseEnrollmentXdpIds,
  getPartnerPageSchemaAdditionalProperties,
  getPartnerPageStats,
  sumPartnerCourseEnrollmentCount,
} from 'bundles/partners-graphql/components/cds/partnerPageStatsUtils';
import PartnerCourseCardsQuery from 'bundles/partners-graphql/components/cds/queries/PartnerCourseCardsQuery.graphql';
import PartnerDegreesQuery from 'bundles/partners-graphql/components/cds/queries/PartnerDegreesQuery.graphql';
import PartnerInfoQuery from 'bundles/partners-graphql/components/cds/queries/PartnerInfoQuery';
import PartnerProductEnrollmentQuery from 'bundles/partners-graphql/components/cds/queries/PartnerProductEnrollmentDocument.graphql';
import PartnerSpecializationsQuery from 'bundles/partners-graphql/components/cds/queries/PartnerSpecializationsQuery.graphql';
import type {
  CdsPartnerInfoQuery,
  CdsPartnerInfoQueryVariables,
} from 'bundles/partners-graphql/components/cds/queries/__generated__/CdsPartnerInfoQuery';
import type {
  PartnerCourseCardsQueryQuery,
  PartnerCourseCardsQueryQueryVariables,
} from 'bundles/partners-graphql/components/cds/queries/__generated__/PartnerCourseCardsQuery';
import type {
  PartnerDegreesQueryQuery,
  PartnerDegreesQueryQueryVariables,
} from 'bundles/partners-graphql/components/cds/queries/__generated__/PartnerDegreesQuery';
import type {
  PartnerProductEnrollmentQueryQuery,
  PartnerProductEnrollmentQueryQueryVariables,
} from 'bundles/partners-graphql/components/cds/queries/__generated__/PartnerProductEnrollmentDocument';
import type {
  PartnerSpecializationsQueryQuery,
  PartnerSpecializationsQueryQueryVariables,
} from 'bundles/partners-graphql/components/cds/queries/__generated__/PartnerSpecializationsQuery';
import PartnerPageGoogleSchemaMarkup from 'bundles/partners-graphql/components/seo/PartnerPageGoogleSchemaMarkup';
import CourseraMetatags from 'bundles/seo/components/CourseraMetatags';
import { getSubdomainDetails } from 'bundles/seo/utils/domainAndMetadataUtils';
import { NotFoundError } from 'bundles/ssr/lib/errors/directives';
import inServerContext from 'bundles/ssr/util/inServerContext';

import _t from 'i18n!nls/partners-graphql';

type PropsFromCaller = {
  params: {
    shortName: string;
  };
};

const PartnerPage = ({ params: { shortName } }: PropsFromCaller) => {
  const location = useLocation();

  // Query for partner info
  const {
    data: partnerData,
    loading: partnerLoading,
    error: partnerError,
  } = useQuery<CdsPartnerInfoQuery, CdsPartnerInfoQueryVariables>(PartnerInfoQuery, {
    variables: {
      shortName,
    },
  });

  const partner = partnerData?.PartnersV1Resource?.shortName?.partners?.[0];
  const catalogCourseIds = partner?.courseIds ?? [];
  const courseIds = catalogCourseIds.slice(0, 1000);
  const specializationIds = partner?.specializations?.elements?.flatMap((s) => (s ? [s.id] : [])) ?? [];

  // Query for course details via ProductCard (includes rating/reviewCount)
  const productCardKeys = courseIds.map((id: string) => ({ id, canonicalType: 'COURSE' as const }));
  const { data: courseCardsData, loading: coursesLoading } = useQuery<
    PartnerCourseCardsQueryQuery,
    PartnerCourseCardsQueryQueryVariables
  >(PartnerCourseCardsQuery, {
    variables: {
      productCardKeys,
    },
    context: {
      clientName: 'gatewayGql',
    },
    skip: courseIds.length === 0,
  });

  const courses = (courseCardsData?.ProductCard?.queryByKeys ?? []).flatMap((card) => {
    if (!card || card.productTypeAttributes.__typename !== 'ProductCard_Course') return [];
    const { rating, reviewCount, canonical } = card.productTypeAttributes;
    return [{ ...canonical, rating, reviewCount }];
  });

  // Query for specialization details from gateway
  const { data: specializationsData, loading: specializationsLoading } = useQuery<
    PartnerSpecializationsQueryQuery,
    PartnerSpecializationsQueryQueryVariables
  >(PartnerSpecializationsQuery, {
    variables: {
      specializationIds,
    },
    context: {
      clientName: 'gatewayGql',
    },
    skip: specializationIds.length === 0,
  });

  const specializations = specializationsData?.Specialization?.queryByIds ?? [];
  const courseEnrollmentProductIds = buildPartnerCourseEnrollmentXdpIds({ courseIds });

  // multiGet silently caps results at Naptime's default page size and never
  // populates paging.next/paging.total for this resource, so pass an explicit
  // limit covering every requested id to get them all back in one request.
  const { data: enrollmentData, loading: enrollmentLoading } = useQuery<
    PartnerProductEnrollmentQueryQuery,
    PartnerProductEnrollmentQueryQueryVariables
  >(PartnerProductEnrollmentQuery, {
    variables: {
      ids: courseEnrollmentProductIds,
      limit: courseEnrollmentProductIds.length,
    },
    skip: courseEnrollmentProductIds.length === 0,
    errorPolicy: 'all',
  });

  const learnerCount = sumPartnerCourseEnrollmentCount(
    enrollmentData?.NaptimeQueries?.XdpV1Resource?.multiGet?.elements ?? []
  );

  // Query for degrees by partner ID
  const { data: degreesData } = useQuery<PartnerDegreesQueryQuery, PartnerDegreesQueryQueryVariables>(
    PartnerDegreesQuery,
    {
      variables: {
        partnerId: partner?.id ?? '',
      },
      context: {
        clientName: 'gatewayGql',
      },
      skip: !partner?.id,
    }
  );

  const allDegrees = degreesData?.Degree?.queryByPartnerId?.elements ?? [];
  const degrees = allDegrees.filter((degree) => user.isSuperuser() || degree.catalogAvailabilityStatus === 'LIVE');

  if (partnerLoading) {
    return <Loading />;
  }

  if (partnerError || !partner) {
    if (inServerContext) {
      throw new NotFoundError();
    }
    return <NotFound />;
  }

  const statsLoading =
    (courseIds.length > 0 && coursesLoading) ||
    (specializationIds.length > 0 && specializationsLoading) ||
    (courseEnrollmentProductIds.length > 0 && enrollmentLoading);

  const { isInternationalSubdomain } = getSubdomainDetails(location);
  const metaTitle = `${partner.name} Online Courses`;

  const instructors = partner.instructors?.elements?.filter((i) => i !== null) ?? [];
  const statsParams = {
    courses,
    courseCount: catalogCourseIds.length,
    specializations,
    learnerCount,
  };
  const partnerStats = statsLoading ? [] : getPartnerPageStats(statsParams);
  const partnerSchemaAdditionalProperties = statsLoading
    ? undefined
    : getPartnerPageSchemaAdditionalProperties(statsParams);

  return (
    <div className="rc-PartnerPage">
      <PartnerHero
        name={partner.name}
        description={partner.description}
        squareLogo={partner.squareLogo}
        stats={partnerStats}
        statsLoading={statsLoading}
      />
      <PageGridContainer>
        <Grid item xs={12}>
          <DegreeCardsSection degrees={degrees} title={_t('Degrees')} />
          <SpecializationCardsSection
            specializations={specializations}
            title={_t('Certificates and Specializations')}
          />
          <CourseCardsSection
            courses={courses}
            title={_t('Courses by #{partnerName}', { partnerName: partner.name })}
          />
          <InstructorsCarouselSection instructors={instructors} />
          <ExploreCategoriesSection />
          <PartnerLinksSection links={partner.links} />
        </Grid>
      </PageGridContainer>
      <div>
        <PartnerPageGoogleSchemaMarkup
          additionalProperties={partnerSchemaAdditionalProperties}
          partner={{
            ...partner,
            courses: {
              elements: courses.map((course) => ({
                __typename: 'CoursesV1',
                id: course.id,
                slug: course.slug,
              })),
            },
            specializations: {
              elements: specializations.map((specialization) => ({
                __typename: 'OnDemandSpecializationsV1',
                id: specialization.id,
                slug: specialization.slug,
              })),
            },
          }}
        />
        <CourseraMetatags
          metaNameAndProperties={{
            title: metaTitle,
            description: partner.description ?? undefined,
            absoluteImageHref: partner.squareLogo ?? undefined,
            useFollowForDisablingIndexing: true,
          }}
          hrefLangOverrides={(links) => (isInternationalSubdomain ? [] : links)}
        />
      </div>
    </div>
  );
};
export default PartnerPage;
